import {
  AppBar,
  SpheraNotification,
  SpheraNotificationGroup,
} from '@sphera-cloud/component-ui-reactjs';
import { SettingsControls, UserSettings } from 'lca-common-ui-components';
import { FC, useEffect, useState } from 'react';
import { KeyCodes } from 'src/common/key-codes';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { infoSelector } from 'src/store/slices/info';
import { fetchUserSettings, updateUserSettings, usersSelector } from 'src/store/slices/users';
import { sendRequest } from 'src/utilities/utilities';
import {
  entitlementsDataSelector,
  fetchUserEntitlements as fetchUserEntitlementsAction,
} from '../../store/slices/authUser';
import VersionInfo from '../VersionInfo';
import headerConfig from './config';
import './CsHeader.css';
interface HeaderProps {
  apiVersion: string;
}
const CsHeader: FC<HeaderProps> = () => {
  const dispatch = useAppDispatch();
  const [showVersion, setShowVersion] = useState(false);
  const [headerValues, setHeaderValues] = useState(structuredClone(headerConfig));
  const [notification, setNotification] = useState(false);
  const info = useAppSelector(infoSelector);
  const user = useAppSelector(usersSelector);
  const entitlements = useAppSelector(entitlementsDataSelector) || {};
  const [userSettings, setUserSettings] = useState<any>(null);
  const [userSettingsVisible, setUserSettingsVisible] = useState(false);
  const settingsControls = SettingsControls;

  const userSettingsHandler = (): void => {
    setUserSettingsVisible(true);
  };

  const saveUserSettings = async (jsonData: any) => {
    setUserSettings(jsonData);
    await dispatch(updateUserSettings(jsonData));
    await dispatch(fetchUserSettings());
  };

  useEffect(() => {
    dispatch(fetchUserEntitlementsAction());
    dispatch(fetchUserSettings());
  }, []);

  const fetchUserEntitlements = async () => {
    const customerId = sessionStorage.getItem('customerId');
    const customerIdString = customerId ? `?customerId=${customerId}` : '';
    if (!customerIdString.length) return entitlements;

    const endpoint = `user/entitlements${customerIdString}`;
    return await sendRequest(endpoint, 'GET').catch(() => {
      setNotification(true);
      setTimeout(() => {
        setNotification(false);
      }, 5000);
    });
  };

  const closeVersionWindow = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    document.removeEventListener('keydown', escFunction, false);
    setShowVersion(false);
  };

  const escFunction = (event: KeyboardEvent): void => {
    if (event.key === KeyCodes.Escape) {
      closeVersionWindow();
    }
  };

  useEffect(() => {
    (async () => {
      if (info.apiVersion === '') return;
      const userEntitlements = await fetchUserEntitlements();
      const newheaderValues = structuredClone(headerConfig);
      newheaderValues.user.name = userEntitlements.user?.displayName ?? '';
      newheaderValues.user.email = userEntitlements.user?.email ?? '';
      newheaderValues.user.company = userEntitlements.user?.customer ?? '';
      newheaderValues.user.menuItems = [{
        label: 'User Settings',
        onClick: userSettingsHandler,
      },
      {
        label: 'Logout',
        url: '/logout',
      }];
      newheaderValues.appSwitcher = userEntitlements?.productLines;

      const helpLink = info.urls.find((item) => item.target === 'Help');
      const helpDeskLink = info.urls.find((item) => item.target === 'HelpDesk');
      const about = newheaderValues.help.links.find((l) => l.id === 'dimaAbout');
      const help = newheaderValues.help.links.find((l) => l.id === 'schHelp');
      const helpDesk = newheaderValues.help.links.find((l) => l.id === 'schHelpDesk');
      if (about)
        about.onClick = () => {
          setShowVersion(true);
        };

      if (help && helpLink)
        help.onClick = () => {
          window.open(helpLink.url, '_blank');
        };

      if (helpDesk && helpDeskLink)
        helpDesk.onClick = () => {
          window.open(helpDeskLink.url, '_blank');
        };
      setHeaderValues(newheaderValues);
    })();
  }, [info, entitlements]);

  useEffect(() => {
    if (user.settings) {
      setUserSettings(JSON.parse(JSON.stringify(user.settings)));
    }
  }, [user]);

  return (
    <div data-testid="header">
      <AppBar entitlementConfig={headerValues} />
      <SpheraNotificationGroup
        position={{
          top: 70,
          right: 30,
        }}
      >
        {notification && (
          <SpheraNotification
            closable={true}
            type="error"
            text="Your tenant has not been correctly set up for this application. Please contact technical support for further information."
            onClose={() => setNotification(false)}
          />
        )}
      </SpheraNotificationGroup>
      {showVersion && <VersionInfo onClose={() => setShowVersion(false)} />}
      {userSettings && (
        <UserSettings
          jsonConfig={userSettings}
          onSave={saveUserSettings}
          settingControls={settingsControls}
          title="User Settings"
          isVisible={userSettingsVisible}
          onClose={setUserSettingsVisible}
          actionText="Save"
          cancelText="Cancel"
        />
      )}
    </div>
  );
};

export default CsHeader;
